import type { ReactNode } from 'react';
import { Shield } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { useMyScopes } from '@/hooks/use-scopes';

/**
 * Fail closed in the UI, the same way the API fails closed.
 *
 * A page that only a planner may act on renders the refusal instead of a form
 * whose Save button is guaranteed to 403. The permission is named, so the
 * person can ask for the right thing rather than "it does not work".
 */
export default function RequireScope({ scope, title, children }: { scope: string; title: string; children: ReactNode }) {
  const me = useMyScopes();
  if (me.isLoading) return <div className="p-6 text-sm text-muted">Loading...</div>;
  if (me.data?.scopes.includes(scope)) return <>{children}</>;
  return (
    <div className="p-6">
      <Card className="max-w-md">
        <div className="flex items-start gap-3">
          <Shield size={18} className="text-red-400 shrink-0 mt-0.5" />
          <div>
            <h2 className="text-sm font-medium text-muted-strong">{title}</h2>
            <p className="text-xs text-muted mt-1">
              This is not part of your job here. It needs the{' '}
              <code className="text-accent-400">{scope}</code> permission, which an owner or
              manager can grant under Admin, People.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
